import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "../../utils"

const badgeVariants = cva(
  "inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
  {
    variants: {
      variant: {
        default:
          "border-transparent bg-primary text-primary-foreground hover:bg-primary/80",
        secondary:
          "border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80",
        destructive:
          "border-transparent bg-destructive text-destructive-foreground hover:bg-destructive/80",
        outline: "text-foreground",
      },
      tone: {
        neutral: "border-transparent bg-muted text-muted-foreground",
        success: "border-transparent bg-green-100 text-green-800",
        warning: "border-transparent bg-yellow-100 text-yellow-800",
        danger:  "border-transparent bg-red-100 text-red-800",
        info:    "border-transparent bg-blue-100 text-blue-800",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

export interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {}

function Badge({ className, variant, tone, ...props }: BadgeProps) {
  return (
    <div className={cn(badgeVariants({ variant, tone }), className)} {...props} />
  )
}

// ── StatusChip ──────────────────────────────────────────────────────────────
// Inline-bordered pill that matches the RR design system exactly.
// Each status has its own bg/fg/border triplet.

type StatusKey =
  // Logistics statuses
  | "draft" | "pending" | "approved" | "under_review" | "confirmed"
  | "cancelled" | "in_transit" | "delivered" | "invoiced" | "waiting"
  | "waiting_for_offer" | "assigned" | "active" | "maintenance"
  // Finance — invoice / bill
  | "sent" | "received" | "partial" | "paid" | "overdue" | "void"
  // Finance — expense
  | "submitted" | "reimbursed" | "rejected"
  // Finance — journal
  | "posted" | "settled";

const STATUS_MAP: Record<StatusKey, { lab: string; bg: string; fg: string; bd: string }> = {
  // Logistics
  draft:            { lab: "Draft",       bg: "#f1f5f9", fg: "#475569", bd: "#cbd5e1" },
  pending:          { lab: "Pending",     bg: "#fef9c3", fg: "#854d0e", bd: "#fde68a" },
  waiting:          { lab: "Waiting",     bg: "#dbeafe", fg: "#1e40af", bd: "#bfdbfe" },
  waiting_for_offer:{ lab: "Waiting",     bg: "#dbeafe", fg: "#1e40af", bd: "#bfdbfe" },
  approved:         { lab: "Approved",    bg: "#dcfce7", fg: "#166534", bd: "#bbf7d0" },
  under_review:     { lab: "In Review",   bg: "#fef9c3", fg: "#854d0e", bd: "#fde68a" },
  confirmed:        { lab: "Confirmed",   bg: "#dcfce7", fg: "#166534", bd: "#bbf7d0" },
  cancelled:        { lab: "Cancelled",   bg: "#fee2e2", fg: "#991b1b", bd: "#fecaca" },
  in_transit:       { lab: "In Transit",  bg: "#f3e8ff", fg: "#6b21a8", bd: "#e9d5ff" },
  delivered:        { lab: "Delivered",   bg: "#dcfce7", fg: "#166534", bd: "#bbf7d0" },
  invoiced:         { lab: "Invoiced",    bg: "#cffafe", fg: "#155e75", bd: "#a5f3fc" },
  assigned:         { lab: "Assigned",    bg: "#dcfce7", fg: "#166534", bd: "#bbf7d0" },
  active:           { lab: "Active",      bg: "#dcfce7", fg: "#166534", bd: "#bbf7d0" },
  maintenance:      { lab: "Maintenance", bg: "#fef9c3", fg: "#854d0e", bd: "#fde68a" },
  // Finance — invoice / bill
  sent:             { lab: "Sent",        bg: "#dbeafe", fg: "#1e40af", bd: "#bfdbfe" },
  received:         { lab: "Received",    bg: "#dbeafe", fg: "#1e40af", bd: "#bfdbfe" },
  partial:          { lab: "Partial",     bg: "#fef3c7", fg: "#92400e", bd: "#fde68a" },
  paid:             { lab: "Paid",        bg: "#dcfce7", fg: "#166534", bd: "#bbf7d0" },
  overdue:          { lab: "Overdue",     bg: "#fee2e2", fg: "#991b1b", bd: "#fecaca" },
  void:             { lab: "Void",        bg: "#f1f5f9", fg: "#94a3b8", bd: "#e2e8f0" },
  // Finance — expense
  submitted:        { lab: "Submitted",   bg: "#f3e8ff", fg: "#6b21a8", bd: "#e9d5ff" },
  reimbursed:       { lab: "Reimbursed",  bg: "#cffafe", fg: "#155e75", bd: "#a5f3fc" },
  rejected:         { lab: "Rejected",    bg: "#fee2e2", fg: "#991b1b", bd: "#fecaca" },
  // Finance — journal
  posted:           { lab: "Posted",      bg: "#dcfce7", fg: "#166534", bd: "#bbf7d0" },
  settled:          { lab: "Settled",     bg: "#e0e7ff", fg: "#3730a3", bd: "#c7d2fe" },
};

export interface StatusChipProps extends React.HTMLAttributes<HTMLSpanElement> {
  status: StatusKey | string;
  label?: string;
  size?: "xs" | "sm" | "md";
}

function StatusChip({ status, label, size = "md", className, ...props }: StatusChipProps) {
  const s = STATUS_MAP[status as StatusKey] ?? STATUS_MAP.draft;
  const pad =
    size === "xs" ? "px-1.5 py-0 text-[10px]" :
    size === "sm" ? "px-2 py-0.5 text-[11px]" :
                    "px-2.5 py-0.5 text-[11.5px]";
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-full font-semibold border tracking-[.01em]",
        pad, className
      )}
      style={{ background: s.bg, color: s.fg, borderColor: s.bd }}
      {...props}
    >
      {label ?? s.lab}
    </span>
  );
}

// ── Pill ──────────────────────────────────────────────────────────────────────
// Generic tonal pill for custom labels (coverage states, tags, etc.)

type PillTone = "slate" | "emerald" | "amber" | "red" | "blue" | "violet" | "indigo" | "cyan";

const PILL_TONES: Record<PillTone, { bg: string; fg: string; bd: string }> = {
  slate:   { bg: "#f1f5f9", fg: "#475569", bd: "#cbd5e1" },
  emerald: { bg: "#dcfce7", fg: "#166534", bd: "#bbf7d0" },
  amber:   { bg: "#fef3c7", fg: "#92400e", bd: "#fde68a" },
  red:     { bg: "#fee2e2", fg: "#991b1b", bd: "#fecaca" },
  blue:    { bg: "#dbeafe", fg: "#1e40af", bd: "#bfdbfe" },
  violet:  { bg: "#f3e8ff", fg: "#6b21a8", bd: "#e9d5ff" },
  indigo:  { bg: "#e0e7ff", fg: "#3730a3", bd: "#c7d2fe" },
  cyan:    { bg: "#cffafe", fg: "#155e75", bd: "#a5f3fc" },
};

export interface PillProps extends React.HTMLAttributes<HTMLSpanElement> {
  tone?: PillTone;
  size?: "xs" | "sm" | "md";
  icon?: React.ElementType;
}

function Pill({ tone = "slate", size = "sm", icon: Icon, children, className, ...props }: PillProps) {
  const t = PILL_TONES[tone];
  const pad =
    size === "xs" ? "px-1.5 py-0 h-[18px] text-[10px]" :
    size === "sm" ? "px-2 py-0.5 text-[11px]" :
                    "px-2.5 py-0.5 text-[12px]";
  const iconSize = size === "xs" ? 9 : 11;
  return (
    <span
      className={cn("inline-flex items-center gap-1 rounded-full font-semibold border leading-none", pad, className)}
      style={{ background: t.bg, color: t.fg, borderColor: t.bd }}
      {...props}
    >
      {Icon && <Icon size={iconSize} />}
      {children}
    </span>
  );
}

// Status mapping for legacy Badge usage
export const statusToneMap: Record<string, NonNullable<BadgeProps["tone"]>> = {
  draft:      "neutral",
  pending:    "neutral",
  available:  "success",
  assigned:   "success",
  in_transit: "info",
  delivered:  "success",
  approved:   "success",
  warning:    "warning",
  maintenance:"warning",
  delayed:    "warning",
  cancelled:  "danger",
  error:      "danger",
  failed:     "danger",
  rejected:   "danger",
}

export { Badge, badgeVariants, StatusChip, Pill }
export type { StatusKey, PillTone }
